import { useCallback, useMemo, useState } from 'react';
import {
  Table,
  Column,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import OwnersChipListAutocomplete from 'shared/components/OwnersChipListAutocomplete';
import TagsChipListAutocomplete from 'shared/components/TagsChipListAutocomplete';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from '../../services/api';
import StringValueEditItem from './StringValueEditItem';
import {
  BulkEditFormData,
  BulkEditArrayProperty,
  BulkEditProperty,
  BulkEditTableItem,
} from './types';
import { getModifiedTableData } from './utils';

type Props = {
  readOnly?: boolean;
  showOnlyModified?: boolean;
  isLoading?: boolean;
};

const BulkEditTable = ({ readOnly, showOnlyModified, isLoading }: Props) => {
  const [loadingModelIds, setLoadingModelIds] = useState<Set<string>>(
    new Set(),
  );
  const { watch, setValue, getValues } = useFormContext<BulkEditFormData>();
  const {
    tableData,
    initialTableData,
    editProperty,
    editPropertyType,
    isGenerateAllLoading,
  } = watch();
  const selectedEnvId = useSelectedEnv();
  const { data: account } = useAccount();

  const getArrayPropertyDiff = useCallback(
    (
      modelId: string,
      property: BulkEditArrayProperty,
      updatedValues: string[],
    ) => {
      const initialTableRowValues =
        initialTableData.find((row) => row.unique_id === modelId)?.[property] ||
        [];

      const addedValues = updatedValues.filter(
        (val) => !initialTableRowValues.includes(val),
      );

      const deletedValues = initialTableRowValues.filter(
        (val) => !updatedValues.includes(val),
      );

      const valuesToShow = distinctArray([
        ...initialTableRowValues,
        ...addedValues,
      ]);

      return { valuesToShow, addedValues, deletedValues };
    },
    [initialTableData],
  );

  const { mutateAsync: generateDescription } = useMutation(
    async (assetId: string) => {
      setLoadingModelIds((prev) => new Set(prev).add(assetId));
      try {
        const res = await api.generateAssetsDescription(
          {
            asset_ids: [assetId],
          },
          selectedEnvId!,
        );
        return res?.[assetId];
      } finally {
        setLoadingModelIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(assetId);
          return newSet;
        });
      }
    },
  );

  const setTableValue = useCallback(
    (
      modelId: string,
      property: BulkEditProperty,
      val: string | string[],
      isGenerated?: boolean,
    ) => {
      // using 'getValues', and not 'tableData' directly - as it causes a race condition
      // when clicking ai generate on rows 1 by 1
      const currentTableData = getValues('tableData');
      setValue(
        'tableData',
        currentTableData.map((model) =>
          model.unique_id === modelId
            ? { ...model, [property]: val, isGenerated }
            : model,
        ),
      );
    },
    [getValues, setValue],
  );

  const handleGenerate = useCallback(
    async (id: string) => {
      const generatedVal = await generateDescription(id);
      setTableValue(id, 'description', generatedVal, true);
    },
    [generateDescription, setTableValue],
  );

  const modifiedTableData = useMemo(
    () =>
      showOnlyModified
        ? getModifiedTableData(
            initialTableData,
            tableData,
            editProperty,
            editPropertyType,
          )
        : [],
    [
      showOnlyModified,
      initialTableData,
      tableData,
      editProperty,
      editPropertyType,
    ],
  );

  const columns = useMemo<Column<BulkEditTableItem>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        renderer: (model) => <OverflowTooltip>{model.name}</OverflowTooltip>,
        filterable: false,
        width: '400px',
      },
      {
        id: 'tags',
        header: 'Tags',
        showValueWhenEmpty: true,
        filterable: false,
        hideColumn: editProperty !== 'tags',
        renderer: ({ unique_id, tags }) => {
          const { valuesToShow, addedValues, deletedValues } =
            getArrayPropertyDiff(unique_id, 'tags', tags);

          return (
            <TagsChipListAutocomplete
              value={valuesToShow}
              readOnly={readOnly}
              newValues={addedValues}
              deletedValues={deletedValues}
              onChange={(val) => setTableValue(unique_id, 'tags', val)}
            />
          );
        },
      },
      {
        id: 'owners',
        header: 'Owners',
        showValueWhenEmpty: true,
        filterable: false,
        hideColumn: editProperty !== 'owners',
        renderer: ({ unique_id, owners }) => {
          const { valuesToShow, addedValues, deletedValues } =
            getArrayPropertyDiff(unique_id, 'owners', owners);

          return (
            <OwnersChipListAutocomplete
              value={valuesToShow}
              readOnly={readOnly}
              newValues={addedValues}
              deletedValues={deletedValues}
              onChange={(val) => setTableValue(unique_id, 'owners', val)}
            />
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        showValueWhenEmpty: true,
        filterable: false,
        hideColumn: editProperty !== 'description',
        renderer: ({ unique_id, description, isGenerated }) => {
          const initialDescription = initialTableData.find(
            (item) => item.unique_id === unique_id,
          )?.description;
          const isRowLoading = loadingModelIds.has(unique_id);

          return (
            <StringValueEditItem
              value={description}
              initialValue={initialDescription}
              readOnly={readOnly}
              onGenerate={() => handleGenerate(unique_id)}
              onChange={(val) => setTableValue(unique_id, 'description', val)}
              isLoading={isGenerateAllLoading || isRowLoading}
              isGenerateEnabled={account?.ai_features_enabled}
              isValueGenerated={isGenerated}
            />
          );
        },
      },
    ],
    [
      account?.ai_features_enabled,
      editProperty,
      getArrayPropertyDiff,
      handleGenerate,
      initialTableData,
      isGenerateAllLoading,
      loadingModelIds,
      readOnly,
      setTableValue,
    ],
  );

  return (
    <Table
      data={showOnlyModified ? modifiedTableData : tableData}
      columns={columns}
      isLoading={isLoading}
    />
  );
};

export default BulkEditTable;
